/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Crown,
  ArrowLeft,
  Sparkles,
  Key,
  Heart,
  Shield,
  Star,
  Award,
  Users,
  CheckCircle2,
  Printer,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTICIPANT_EXPECTATIONS = [
  "Treat mentors, volunteers, participants, and guests with respect.",
  "Demonstrate kindness, honesty, and integrity.",
  "Use appropriate language and behavior at all times.",
  "Participate positively and actively in program activities.",
  "Refrain from bullying, gossiping, teasing, fighting, or excluding others.",
  "Follow all program rules, directions, and safety guidelines.",
  "Represent Queenz Like Me with pride both in person and online.",
];

const PARENT_EXPECTATIONS = [
  "Support my child's participation and growth throughout the program.",
  "Encourage respectful behavior and accountability.",
  "Maintain open communication with program leadership.",
  "Ensure emergency contact information remains current.",
  "Treat mentors, volunteers, participants, and families with courtesy and respect.",
];

const VALUES = [
  { label: "Respect", icon: Crown, color: "#d62288" },
  { label: "Growth", icon: Sparkles, color: "#7d289b" },
  { label: "Responsibility", icon: Key, color: "#d4af37" },
  { label: "Leadership", icon: Crown, color: "#d62288" },
  { label: "Confidence", icon: Sparkles, color: "#7d289b" },
  { label: "Integrity", icon: Key, color: "#d4af37" },
  { label: "Empowerment", icon: Crown, color: "#d62288" },
  { label: "Kindness", icon: Sparkles, color: "#7d289b" },
];

const DISCIPLINE = [
  "Verbal warning and discussion.",
  "Parent/guardian notification.",
  "Behavioral improvement plan.",
  "Temporary suspension from activities.",
  "Removal from the program for repeated or serious violations.",
];

function Section({ title, eyebrow, icon: Icon, children }) {
  return (
    <section className="mt-12 first:mt-0">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="h-11 w-11 rounded-2xl flex items-center justify-center"
          style={{
            background: "rgba(214, 34, 136, 0.1)",
            border: "1.5px solid rgba(214, 34, 136, 0.35)",
          }}
        >
          <Icon className="h-5 w-5 text-[#d62288]" />
        </div>
        <div>
          {eyebrow && (
            <p className="font-script text-xl gold-text leading-none">
              {eyebrow}
            </p>
          )}
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#4a1466] leading-tight">
            {title}
          </h2>
        </div>
      </div>
      <div className="prose-content text-[#1a0f14] text-base sm:text-[17px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function AgreementPage() {
  const [form, setForm] = useState({
    participant_name: "",
    participant_signature: "",
    parent_name: "",
    parent_signature: "",
    parent_email: "",
    emergency_contact: "",
    media_release: false,
    acknowledged: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged) {
      toast.error("Please acknowledge the agreement to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/agreements`, form);
      setSuccess(true);
      toast.success("Agreement signed and saved ♕");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="agreement-page"
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #fff5e0 0%, #fff9fb 30%, #fce6f0 100%)",
      }}
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[#f0e1e8] print:hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="agreement-back-home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#7d289b] hover:text-[#d62288] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Queenz Like Me
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="agreement-print"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4af37] text-[#7d289b] font-semibold text-sm hover:bg-[#d4af37] hover:text-white transition-colors"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-14 sm:py-20">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <img
            src="/logo-sm.png"
            alt="Queenz Like Me"
            data-testid="agreement-logo"
            className="mx-auto h-24 w-24 sm:h-28 sm:w-28 rounded-full ring-4 ring-[#d4af37]/40 shadow-xl mb-6"
          />
          <p className="font-script text-2xl gold-text mb-1">
            Queen<span className="font-display not-italic">z</span> Like Me
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#4a1466] leading-tight">
            Parent &amp; Participant Agreement
          </h1>
          <p className="mt-4 text-sm uppercase tracking-[0.2em] text-[#7d289b]/80 font-semibold">
            Please read carefully · Sign electronically below
          </p>
        </motion.div>

        {/* Document card */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-3xl shadow-xl border border-[#f0e1e8] p-7 sm:p-12 print:shadow-none print:border-none"
        >
          {/* Welcome */}
          <Section title="Welcome to Queenz Like Me" eyebrow="Introduction" icon={Heart}>
            <p>
              Queenz Like Me is dedicated to empowering young girls to
              recognize their value, embrace their unique gifts, and develop
              into confident, purpose-driven leaders. Through mentorship,
              personal development, life skills, and positive relationships,
              participants are encouraged to grow academically, socially,
              emotionally, and spiritually.
            </p>
            <p className="mt-4">
              At Queenz Like Me, every girl is{" "}
              <span className="font-semibold text-[#d62288]">heard</span>, every
              girl is{" "}
              <span className="font-semibold text-[#d62288]">seen</span>, and
              every girl is encouraged to become{" "}
              <span className="font-semibold text-[#d62288]">unstoppable</span>.
            </p>
          </Section>

          {/* Participant Expectations */}
          <Section title="Participation Expectations" eyebrow="The Queen's Promise" icon={Star}>
            <p>As a participant of Queenz Like Me, I agree to:</p>
            <ul className="mt-4 space-y-3">
              {PARTICIPANT_EXPECTATIONS.map((item) => (
                <li
                  key={item}
                  data-testid="participant-expectation"
                  className="flex items-start gap-3"
                >
                  <Crown className="h-4 w-4 mt-1 shrink-0 text-[#d62288]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Parent Expectations */}
          <Section
            title="Parent / Guardian Expectations"
            eyebrow="Partners in Purpose"
            icon={Users}
          >
            <p>As a parent / guardian, I agree to:</p>
            <ul className="mt-4 space-y-3">
              {PARENT_EXPECTATIONS.map((item) => (
                <li
                  key={item}
                  data-testid="parent-expectation"
                  className="flex items-start gap-3"
                >
                  <Crown className="h-4 w-4 mt-1 shrink-0 text-[#7d289b]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Program Values */}
          <Section title="Program Values" eyebrow="Our Eight Pillars" icon={Award}>
            <p>
              These values reflect the journey of every participant as she
              progresses through the{" "}
              <span className="font-semibold text-[#d4af37]">Key</span>,{" "}
              <span className="font-semibold text-[#d62288]">Butterfly</span>,
              and{" "}
              <span className="font-semibold text-[#7d289b]">Crown</span> levels
              of Queenz Like Me.
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {VALUES.map((v) => (
                <div
                  key={v.label}
                  data-testid={`value-${v.label.toLowerCase()}`}
                  className="rounded-2xl p-4 text-center"
                  style={{
                    background: `${v.color}10`,
                    border: `1.5px solid ${v.color}55`,
                  }}
                >
                  <v.icon
                    className="h-6 w-6 mx-auto mb-2"
                    style={{ color: v.color }}
                  />
                  <p
                    className="font-display font-bold text-sm sm:text-base"
                    style={{ color: v.color }}
                  >
                    {v.label}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          {/* Disciplinary Procedures */}
          <Section
            title="Disciplinary Procedures"
            eyebrow="Keeping Every Queen Safe"
            icon={Shield}
          >
            <p>
              To maintain a safe and positive environment, the following actions
              may be taken when expectations are not met:
            </p>
            <ol className="mt-4 space-y-2.5 list-decimal list-inside marker:text-[#d62288] marker:font-bold">
              {DISCIPLINE.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ol>
            <div className="mt-5 p-4 rounded-2xl border-l-4 border-[#d62288] bg-[#fdf1f7]">
              <p className="text-sm sm:text-base">
                <span className="font-semibold text-[#d62288]">
                  Immediate removal
                </span>{" "}
                may occur for violence, threats, bullying, harassment,
                possession of prohibited items, or behavior that jeopardizes the
                safety of others.
              </p>
            </div>
          </Section>

          {/* Pledge */}
          <Section title="The Queenz Like Me Pledge" eyebrow="Speak it. Live it." icon={Sparkles}>
            <div className="mt-2 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#4a1466] via-[#7d289b] to-[#d62288] text-white text-center">
              <Crown className="h-7 w-7 mx-auto text-[#f6e27a] mb-3" />
              <p className="font-display text-xl sm:text-2xl leading-relaxed italic">
                "I am a Queen. I am valuable. I am capable. I am confident.
                I am heard. I am seen. I am unstoppable."
              </p>
              <p className="mt-4 text-sm uppercase tracking-[0.2em] text-[#f6e27a]">
                Founder · Lacey Fincher
              </p>
            </div>
          </Section>
        </motion.article>

        {/* Signing card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 bg-white rounded-3xl shadow-xl border border-[#f0e1e8] p-7 sm:p-12 print:shadow-none"
          id="sign"
        >
          {success ? (
            <div data-testid="agreement-success" className="text-center py-8">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9 text-[#d4af37]" />
              </div>
              <h2 className="mt-5 font-display text-3xl font-bold text-[#4a1466]">
                Agreement Signed ♕
              </h2>
              <p className="mt-3 text-[#5c4a54] max-w-xl mx-auto">
                Thank you for signing the Parent &amp; Participant Agreement.
                A copy has been saved with our team. We can't wait to welcome
                your queen into the sisterhood.
              </p>
              <Link
                to="/"
                data-testid="agreement-return-home"
                className="btn-primary inline-flex mt-7"
              >
                Return Home
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <p className="font-script text-2xl gold-text mb-1">
                  Acknowledgment
                </p>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#4a1466]">
                  Sign the Agreement
                </h2>
                <p className="mt-3 text-[#5c4a54] max-w-xl mx-auto">
                  We have read and understand the expectations, policies, and
                  values of Queenz Like Me, and we agree to uphold them.
                </p>
              </div>

              <form
                onSubmit={submit}
                data-testid="agreement-form"
                className="space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="ql-label">Participant name</label>
                    <input
                      required
                      type="text"
                      data-testid="agreement-participant-name"
                      className="ql-input"
                      placeholder="Daughter's full name"
                      value={form.participant_name}
                      onChange={update("participant_name")}
                    />
                  </div>
                  <div>
                    <label className="ql-label">Participant signature</label>
                    <input
                      required
                      type="text"
                      data-testid="agreement-participant-signature"
                      className="ql-input font-script text-xl"
                      placeholder="Type her full name as signature"
                      value={form.participant_signature}
                      onChange={update("participant_signature")}
                    />
                  </div>
                  <div>
                    <label className="ql-label">Parent / Guardian name</label>
                    <input
                      required
                      type="text"
                      data-testid="agreement-parent-name"
                      className="ql-input"
                      placeholder="Your full name"
                      value={form.parent_name}
                      onChange={update("parent_name")}
                    />
                  </div>
                  <div>
                    <label className="ql-label">Parent / Guardian signature</label>
                    <input
                      required
                      type="text"
                      data-testid="agreement-parent-signature"
                      className="ql-input font-script text-xl"
                      placeholder="Type your full name as signature"
                      value={form.parent_signature}
                      onChange={update("parent_signature")}
                    />
                  </div>
                  <div>
                    <label className="ql-label">Parent email</label>
                    <input
                      required
                      type="email"
                      data-testid="agreement-parent-email"
                      className="ql-input"
                      placeholder="you@example.com"
                      value={form.parent_email}
                      onChange={update("parent_email")}
                    />
                  </div>
                  <div>
                    <label className="ql-label">Emergency contact number</label>
                    <input
                      required
                      type="tel"
                      data-testid="agreement-emergency"
                      className="ql-input"
                      placeholder="(555) 555-1234"
                      value={form.emergency_contact}
                      onChange={update("emergency_contact")}
                    />
                  </div>
                </div>

                {/* Photo Release */}
                <div className="rounded-2xl border-2 border-[#f0e1e8] p-5">
                  <p className="font-display font-semibold text-[#4a1466] text-base">
                    Photo &amp; Media Release
                  </p>
                  <p className="mt-2 text-sm text-[#5c4a54] leading-relaxed">
                    I grant permission for photographs and/or video recordings
                    of my child to be used by Queenz Like Me for educational,
                    promotional, social media, website, marketing, and
                    program-related purposes.
                  </p>
                  <label className="mt-4 inline-flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="agreement-media-release"
                      checked={form.media_release}
                      onChange={update("media_release")}
                      className="h-5 w-5 rounded border-2 border-[#d4af37] accent-[#d62288] cursor-pointer"
                    />
                    <span className="text-sm font-medium text-[#1a0f14]">
                      <span className="font-semibold">YES</span> — I grant
                      permission. (Leave unchecked for NO.)
                    </span>
                  </label>
                </div>

                {/* Acknowledgment checkbox */}
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    required
                    type="checkbox"
                    data-testid="agreement-acknowledged"
                    checked={form.acknowledged}
                    onChange={update("acknowledged")}
                    className="h-5 w-5 mt-0.5 rounded border-2 border-[#d62288] accent-[#d62288] cursor-pointer"
                  />
                  <span className="text-sm text-[#1a0f14] leading-relaxed">
                    We have read and understand the expectations, policies, and
                    values of Queenz Like Me. We agree to support and uphold
                    the mission of creating a safe, respectful, empowering, and
                    positive environment for all participants.
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="agreement-submit"
                  disabled={loading}
                  className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Signing…" : "Sign the Agreement"}
                  {!loading && <Crown className="h-4 w-4" />}
                </button>

                <p className="text-xs text-[#7c6470]">
                  Digital signing is binding. A timestamped copy will be saved
                  with the Queenz Like Me team.
                </p>
              </form>
            </>
          )}
        </motion.div>

        {/* Footer note */}
        <p className="mt-10 text-center text-sm text-[#7c6470] print:mt-4">
          ♕ Queenz Like Me · Building confident, courageous &amp; purposeful
          young Queenz
        </p>
      </main>
    </div>
  );
}
